import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CargoItemRes, CargoItemStatusRes, CargoRes } from 'api/swagger';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data.ts';
import { OrderStatus } from 'types/order.ts';
import { CargoItemStatus } from 'components/cargos/cargo-item-status';
import { CargoItemCheckbox } from 'components/cargos/cargos-list/components/cargo-item-checkbox';
import { useGetCargoItems } from '../../../../../api/cargo-items/use-get-cargo-items.ts';
import styles from './cargo-items-table.module.scss';

interface ICargoItemsTableProps {
  cargo: CargoRes;
  withoutCheckbox?: boolean;
  activeIds?: number[] | null;
  actionsController?: (record: ICargoItemsTableData) => React.ReactNode;
}

export interface ICargoItemsTableData extends CargoItemRes {
  key: React.Key;
}

export const CargoItemsTable: React.FC<ICargoItemsTableProps> = (props) => {
  const { cargo, withoutCheckbox = false, activeIds, actionsController } = props;
  const { data: cargoItems, isLoading } = useGetCargoItems({ cargoId: cargo.id });

  const tableData = useTransformToTableData(cargoItems?.data || []);
  const { t } = useTranslation();

  const allowSelectItems = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.transport_manager]);

  const columns: ColumnsType<CargoItemRes> = [
    ...(withoutCheckbox || !allowSelectItems
      ? []
      : [
          {
            title: '',
            dataIndex: 'id',
            key: 'id',
            width: '34px',
            render: (_: number, cargoItem: CargoItemRes) => <CargoItemCheckbox cargoItem={cargoItem} cargo={cargo} />,
          },
        ]),
    { title: 'ID', dataIndex: 'compositeId', key: 'compositeId' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: CargoItemStatusRes) => {
        return <CargoItemStatus type={status.type} from={status.fromWarehouseId} to={status.toWarehouseId} />;
      },
    },
    { title: t('cargo:length_label'), dataIndex: 'length', key: 'length' },
    { title: t('cargo:width_label'), dataIndex: 'width', key: 'width' },
    { title: t('cargo:height_label'), dataIndex: 'height', key: 'height' },
    { title: t('cargo:stacks_label'), dataIndex: 'stack', key: 'stack' },
    {
      title: t('cargo:weight_label'),
      dataIndex: 'weight',
      key: 'weight',
      render: (weight: number) => {
        return weight ? weight : <span className={styles.red}>0</span>;
      },
    },
    { title: t('cargo:ldm_label'), dataIndex: 'LDM', key: 'LDM' },
    { title: t('cargo:volume_label'), dataIndex: 'volume', key: 'volume' },
    ...(actionsController
      ? [
          {
            title: '',
            dataIndex: 'action',
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            render: (_, record: CargoItemRes) => actionsController(record),
          },
        ]
      : []),
  ];

  return (
    <Table
      className={cargo?.order?.status === OrderStatus.cancelled ? styles.canceled : ''}
      rowClassName={(record) => (activeIds && !activeIds.includes(record.id) ? styles.opacityRow : '')}
      columns={columns}
      loading={isLoading}
      dataSource={tableData}
      pagination={false}
    />
  );
};
